import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import axiosConfig from "../../utils/axiosConfig";

const VisioRecapView = ({ onDone }) => {
  const { token } = useParams();
  const [pictures, setPictures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPicture, setSelectedPicture] = useState(null);

  const handleDownloadAll = () => {
    pictures.forEach((picture, index) => {
      setTimeout(() => {
        const link = document.createElement("a");
        link.href = picture.base64;
        const ext = picture.type ? picture.type.split("/")[1] : "jpeg";
        link.download = `expertise_photo_${index + 1}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 300);
    });
  };

  useEffect(() => {
    const fetchPictures = async () => {
      try {
        const res = await axiosConfig("folder/get-pictures-client", {
          data: { token },
        });
        if (res.data.success) {
          setPictures(res.data.pictures);
        }
      } catch (err) {
        console.error("Erreur lors de la récupération des photos:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPictures();
  }, [token]);

  return (
    <>
      <Card className="bg-white shadow-lg border-0 overflow-hidden m-4">
        <div className="p-6">
          <div className="space-y-4">
            {/* Header */}
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  ></path>
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Visioconférence terminée
              </h2>
              <p className="text-sm text-gray-500">
                Merci pour votre participation
              </p>
            </div>

            {/* Photos */}
            {isLoading ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-sm text-gray-500">
                  Chargement des photos...
                </p>
              </div>
            ) : pictures.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700 text-center">
                  {pictures.length} photo{pictures.length > 1 ? "s" : ""} prise
                  {pictures.length > 1 ? "s" : ""} lors de l'expertise
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {pictures.map((picture) => (
                    <div
                      key={picture.id}
                      className="relative aspect-[4/3] rounded-md overflow-hidden cursor-pointer border border-gray-200 hover:border-blue-400 transition-colors"
                      onClick={() => setSelectedPicture(picture)}
                    >
                      <img
                        src={picture.base64}
                        alt={`Photo ${picture.id}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 bg-gray-50 rounded-md">
                <svg
                  className="w-10 h-10 text-gray-300 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  ></path>
                </svg>
                <p className="text-sm text-gray-500">
                  Aucune photo n'a été prise lors de cette visioconférence
                </p>
              </div>
            )}

            {/* Buttons */}
            <div className="space-y-2">
              {pictures.length > 0 && (
                <Button
                  onClick={handleDownloadAll}
                  className="w-full h-10 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    ></path>
                  </svg>
                  Télécharger les photos
                </Button>
              )}
              <Button
                onClick={onDone}
                variant="outline"
                className="w-full h-10 text-sm font-medium rounded-md transition-colors"
              >
                Terminer
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Lightbox modal */}
      {selectedPicture && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedPicture(null)}
        >
          <div className="relative max-w-full max-h-full">
            <button
              className="absolute -top-10 right-0 text-white text-sm font-medium hover:text-gray-300"
              onClick={() => setSelectedPicture(null)}
            >
              Fermer &times;
            </button>
            <img
              src={selectedPicture.base64}
              alt={`Photo ${selectedPicture.id}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default VisioRecapView;
